import { getAuthToken } from "../auth/clerk";

// Auth header injection (issue #464):
//
// Every fetch in this module awaits `getAuthToken()` and adds an
// `Authorization: Bearer <token>` header when a token is available.
// `getAuthToken()` returns null when Clerk isn't configured (dev-bypass
// build) or when the user isn't signed in, in which case we send the
// request unauthenticated and let the backend's dev-bypass / 401 handling
// decide. Callers don't need to know about Clerk; they just `await api()`
// and the auth plumbing happens here.
async function authHeaders(): Promise<HeadersInit> {
  const token = await getAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

export async function authHeadersOnly(): Promise<Record<string, string>> {
  // Variant for callers that already have their own Content-Type header
  // (e.g. SSE pump). Returns just the optional Authorization header so
  // the caller can spread it into their own header dict.
  const token = await getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// API base URL.  Resolution order:
//   1. window.LUCID_API_URL (set by /config.js — preserves the
//      runtime-config story the vanilla UI used).
//   2. import.meta.env.VITE_API_BASE (build-time, set by Dockerfile).
//   3. "" (same-origin — used by `vite dev` proxy).
export function apiBase(): string {
  let base: string;
  if (typeof window !== "undefined" && window.LUCID_API_URL) {
    base = window.LUCID_API_URL;
  } else {
    base = import.meta.env.VITE_API_BASE ?? "";
  }
  // Strip trailing slashes so callers can concatenate "/api/..." paths
  // without producing accidental "//api/..." double slashes.
  return base.replace(/\/$/, "");
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export async function api<T = unknown>(
  path: string,
  method: "GET" | "POST" = "GET",
  body: unknown = null,
): Promise<T> {
  const opts: RequestInit = {
    method,
    headers: await authHeaders(),
    credentials: "include",
  };
  if (body) opts.body = JSON.stringify(body);
  const resp = await fetch(`${apiBase()}${path}`, opts);
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const err = await resp.json();
      if (err && typeof err === "object" && "detail" in err) {
        detail = (err as { detail?: string }).detail || detail;
      }
    } catch {
      /* ignore */
    }
    throw new ApiError(detail || `HTTP ${resp.status}`, resp.status);
  }
  return (await resp.json()) as T;
}

export interface AvailableExit {
  direction: string;
  target_location_id: string;
  target_label?: string | null;
}

// Issue #493: NPCs at the player's current location, for the do/say
// intent toggle's optional "say to" dropdown.
export interface PresentNPC {
  entity_id: string;
  name: string;
}

// Issue #545: takeable items at the player's current location, for
// the "Take" quick-action chip's drop-up popover.
export interface TakeableItem {
  entity_id: string;
  name: string;
}

export interface SessionResponse {
  session_id?: string;
  turn: number;
  narration: string;
  world_title?: string;
  location_name?: string;
  ambient_image_url?: string | null;
  ambient_audio_url?: string | null;
  history?: HistoryEntry[];
  // Issue #444: per-location exits for the quick-actions strip.
  available_exits?: AvailableExit[];
  // Issue #493: NPCs present at the current location, for the do/say
  // toggle's "say to" dropdown.
  present_npcs?: PresentNPC[];
  // Issue #545: takeable items at the current location, for the
  // "Take" quick-action chip's drop-up popover.
  takeable_entities?: TakeableItem[];
}

export interface HistoryEntry {
  action: string;
  narration?: string | null;
}

// Issue #641: typed engine→player non-diegetic message. Mirrors the
// Pydantic ``SystemMessage`` in src/lucid/models/system_messages.py.
// v1 just renders ``body``; ``choices`` is wired through for the
// disambiguation work to plug in later without a schema bump.
export interface SystemMessage {
  kind: "clarification" | "notice" | "prompt";
  body: string;
  choices?: string[] | null;
  id?: string | null;
}

export interface TurnDoneMeta {
  new_turn: number;
  location_name?: string;
  ambient_image_url?: string | null;
  ambient_audio_url?: string | null;
  sfx_url?: string | null;
  is_investigation?: boolean;
  // Issue #444: see ``SessionResponse.available_exits``.
  available_exits?: AvailableExit[];
  // Issue #493: see ``SessionResponse.present_npcs``.
  present_npcs?: PresentNPC[];
  // Issue #545: see ``SessionResponse.takeable_entities``.
  takeable_entities?: TakeableItem[];
  // Issue #641: typed engine→player non-diegetic messages emitted by
  // facets this turn. Empty/absent when no facet emitted any.
  system_messages?: SystemMessage[];
}

// Issue #493: do/say intent toggle in the input form.
// Issue #605: ``examine`` is now its own sticky mode alongside Do/Say
// — submitting with ``intent="examine"`` routes the turn through the
// engine's read-only investigation path (entity-focused on_read fan-in
// per #592) instead of the action router.
export type Intent = "do" | "say" | "examine";

// Issue #535: multi-world picker types — mirrors the Pydantic models in
// src/lucid/server/models.py (WorldSummary, WorldListResponse,
// SessionByWorldResponse, CreateSessionByWorldRequest).
export interface WorldSummary {
  world_name: string;
  title?: string | null;
  blurb?: string | null;
  start_location?: string | null;
  coming_soon?: boolean;
}

export interface WorldListResponse {
  worlds: WorldSummary[];
}

export interface SessionByWorldResponse {
  session_id: string;
  world_name: string;
  narration: string;
  turn: number;
  ambient_image_url?: string | null;
  ambient_audio_url?: string | null;
  world_title?: string | null;
  available_exits?: AvailableExit[];
  present_npcs?: PresentNPC[];
  takeable_entities?: TakeableItem[];
}

// localStorage key used by the picker + GameContext to remember the
// last world a player chose, so subsequent visits to /play land in the
// same session instead of bouncing back to the picker.
export const LAST_WORLD_STORAGE_KEY = "lucid.lastWorldName";

// Issue #499: typed quick-action payloads for chip-driven turns.
// The discriminator is ``kind``. Future variants (``take`` for #492,
// ``say`` for #493) add new members to this union without disturbing
// the existing wiring.
export interface MoveQuickAction {
  kind: "move";
  direction: string;
}

export type QuickAction = MoveQuickAction;
